import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Iterable } from 'immutable';
import { isEqual, cloneDeep, isEqualWith } from 'lodash';
import Chart from 'chart.js';

// Chart plugins
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
// eslint-disable-next-line
import datalabelsPlugin from 'chartjs-plugin-datalabels';

// Libs
import { getChartFromCatalog, getChartType, calculateDatasets, calculateOptions, insertPluginsOptions } from '../../lib/chartjs-lib';
// import * as exportLib from './../../../content/exports/exports-lib';
// TODO: Reactivate export functionality when export library and serverside are ready

export default class ChartComponent extends Component {
    constructor() {
        super();

        this.state = {
            update: false,
            redraw: false,
            pluginsApplied: false
        };
    }

    componentWillMount() {
        this.chart_instance = undefined;
        if (this.props.data && this.props.config && this.props.type) {
            let propsData, data, type, options, chartType, height = null;
            if (Iterable.isIterable(this.props.data)) {
                propsData = this.props.data.toJS();
            } else {
                propsData = cloneDeep(this.props.data);
            }

            if (this.props.config.graph && this.props.config.graph.plugins && this.props.config.graph.plugins.length > 0) {
                this.registerPlugins(this.props.config.graph.plugins);
            }

            // Checks if there is some data in array given from props
            if (propsData.length > 0) {
                data = calculateDatasets(propsData, this.props.config.data.metrics, this.props.config.data.dimension, this.props.type, this.props.config.data.breakBy);
                options = calculateOptions(this.props.config);

                if (this.props.type) {
                    type = getChartType(this.props.type);
                    chartType = getChartFromCatalog(this.props.type).value;
                }
                if (!!this.props.height) {
                    height = this.props.height;
                }
                this.setState({
                    data: data,
                    type: type,
                    chartType: chartType,
                    options: options,
                    height: height
                });
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.data && nextProps.config && nextProps.type) {
            let propsData, data, type, options, chartType, height = null;
            if (Iterable.isIterable(nextProps.data)) {
                propsData= nextProps.data.toJS();
            } else {
                propsData = cloneDeep(nextProps.data);
            }

            // Checks if there is some data in array given from nextProps
            if (propsData.length > 0) {
                let update = false, redraw = false;
                data = calculateDatasets(propsData, nextProps.config.data.metrics, nextProps.config.data.dimension, nextProps.type, nextProps.config.data.breakBy);
                options = calculateOptions(nextProps.config);

                if (nextProps.type) {
                    type = getChartType(nextProps.type);
                    chartType = getChartFromCatalog(nextProps.type).value;
                }

                // Check if data calculated by libs is the same data that a previously version stored in state
                // and set the redraw and update flags if proceed.
                // Update flag is true if datasets has changed
                // (update chart instance to get the updated info)
                // Redraw flag is true if global chart type, metrics/dimension chart config or chart options has changed
                // (destroy chart and render as a new instance)
                if (this.state.data && this.state.options && this.state.type) {
                    if (this.state.data.datasets.length !== data.datasets.length || !isEqual(this.state.data.labels, data.labels)) {
                        update = true;
                    } else {
                        this.state.data.datasets.forEach((dataset, index) => {
                            if (!isEqualWith(dataset, data.datasets[index], (value1, value2) => {
                                    let result = true;
                                    if (value1 && value2) {
                                        Object.keys(value2).forEach(key => {
                                            if (!isEqual(value1[key], value2[key])) {
                                                result = false;
                                            }
                                        });
                                    } else {
                                        result = false;
                                    }
                                    return result;
                                })) {
                                update = true;
                            }
                        });
                    }
                    if (JSON.stringify(options) !== JSON.stringify(this.state.options) ||
                        !isEqual(nextProps.config.data.metrics, this.props.config.data.metrics) ||
                        !isEqual(nextProps.config.data.dimension, this.props.config.data.dimension) ||
                        type !== this.state.type ||
                        chartType !== this.state.chartType) {

                        redraw = true;
                    }
                }

                if (!!this.props.height && this.props.height !== nextProps.height) {
                    height = nextProps.height;
                    redraw = true;
                } else {
                    height = this.props.height;
                }

                this.setState({
                    data: data,
                    type: type,
                    chartType: chartType,
                    options: options,
                    update: update,
                    redraw: redraw,
                    height: height
                });
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { redraw, update, type, data, options } = nextState;
        if (this.chart_instance) {
            return !!(redraw || update);
        } else {
            return !!(type && data && options);
        }
    }

    componentDidMount() {
        if (this.state.type && this.state.data && this.state.options) {
            this.renderChart();
        }
    }

    componentDidUpdate() {
        if (this.state.type && this.state.data && this.state.options) {

            // Check redraw and update flags for chart instance management
            // If redraw if true, destroy and create a new chart instance
            // If update is true, call the update method from chart instance
            // If both flags are false (default values), check if instance is undefined to create a new chart
            if (!this.chart_instance) {
                this.renderChart();
            } else if (this.state.update) {
                this.updateChart();
            } else if (this.state.redraw) {
                this.redrawChart();
            }
        }
    }

    componentWillUnmount() {
        if (this.chart_instance) {
            this.destroyChart();
        }
    }

    updateChart() {

        // Update chart data instance
        let { redraw } = this.state;
        if (redraw) {
            this.redrawChart();
        } else {
            let options = Object.assign({}, this.state.options);
            if (this.props.config.graph.plugins && this.props.config.graph.plugins.length > 0 && !this.state.pluginsApplied) {
                options = insertPluginsOptions(this.props.data, this.props.config, this.state.options, this.chart_instance);
                redraw = true;
            }

            this.chart_instance.config.data = Object.assign({}, this.state.data);
            this.chart_instance.update();
            this.setState({
                options: options,
                update: false,
                redraw: redraw,
                pluginsApplied: true
            });
        }
    }

    destroyChart() {
        this.chart_instance.destroy();
    }

    redrawChart() {
        let { update } = this.state;
        if (update) { update = false; }
        this.destroyChart();
        this.renderChart();
        this.setState({
            redraw: false,
            update: update
        });
    }

    renderChart() {
        let {data, type, options} = this.state;
        const node = document.getElementById(this.props.config.graph.id).getContext('2d');

        this.chart_instance = new Chart(node, {
            type,
            data,
            options
        });
        if (this.props.config.graph.plugins && this.props.config.graph.plugins.length > 0 && !this.state.pluginsApplied) {
            options = insertPluginsOptions(this.props.data, this.props.config, this.state.options, this.chart_instance);
            let redraw = true;

            this.setState({
                options: options,
                redraw: redraw,
                pluginsApplied: true
            });
        }
    }

    registerPlugins(plugins) {

        // Register all plugins enabled at config object given by props
        // Right now the only two plugins we have in our catalog are registered globally
        // to be available for all charts on the same page
        plugins.forEach((plugin) => {
            switch (plugin.name) {
                case 'zoom':
                    Chart.plugins.register(zoomPlugin);
                    break;
                case 'annotation':
                    Chart.plugins.register(annotationPlugin);
                    break;
                default:
                    break;
            }
        });
    }

    // TODO: enable export to PDF functionality when export library and serverside are ready
    printChart() {
        // let img = this.chart_instance.toBase64Image(),
        //     height = this.chart_instance.height,
        //     width = this.chart_instance.width;
        //
        // let config = exportLib.configPdfOptions({
        //     title: this.props.config.graph.title
        // });
        // let render = exportLib.renderChartImage(img, height, width, config);
        // if (this.props.config.graph.print) {
        //     this.props.config.graph.print(render, config);
        // }
    }

    handleOnClick(event, chart_instance) {
        const instance = chart_instance;

        const {
            getDatasetAtEvent,
            getElementAtEvent,
            getElementsAtEvent,
            onElementsClick
        } = this.props;

        getDatasetAtEvent && getDatasetAtEvent(instance.getDatasetAtEvent(event));
        getElementAtEvent && getElementAtEvent(instance.getElementAtEvent(event));
        getElementsAtEvent && getElementsAtEvent(instance.getElementsAtEvent(event));
        onElementsClick && onElementsClick(instance.getElementsAtEvent(event)); // Backward compatibility
    }

    render() {
        // let minWidth = chartjsLib.getChartType(this.props.type).toString() === 'pie' || chartjsLib.getChartType(this.props.type).toString() === 'doughnut' ? 250 : 800;

        let axisTitles = null;

        if (this.props.config.graph.leftAxisLabel || this.props.config.graph.rightAxisLabel) {
            axisTitles = (
                <div style={{ /*minWidth: 800, */width: '100%', height: 20, marginBottom: 10 }}>
                    {this.props.config.graph.leftAxisLabel ?
                        <div className="pull-left" style={{ width: '50%', textAlign: 'left' }}>{this.props.config.graph.leftAxisLabel}</div>
                        : null}
                    {this.props.config.graph.rightAxisLabel ?
                        <div className="pull-right" style={{ width: '50%', textAlign: 'right' }}>{this.props.config.graph.rightAxisLabel}</div>
                        : null}
                </div>
            );
        }

        return (
            <div style={{ /*overflowX: 'auto'*/ }}>
                {this.props.config.graph.title ? (
                    <div style={{/*minWidth: 800, */width: '100%', height: 25, marginBottom: 5}}>
                        <div className="pull-left" style={{fontWeight: 500, fontSize: 16}}>
                            {this.props.config.graph.title}
                        </div>
                    </div>
                ) : null}
                {axisTitles}
                <div id="canvasContainer" style={{ /*minWidth: 800*/ minHeight: 0, minWidth: 0 }}>
                    <canvas
                        id={this.props.config.graph.id}
                        height={this.state.height || this.props.config.graph.height}
                        onClick={this.handleOnClick(this.chart_instance)}
                    />
                </div>
            </div>
        );
    }
}

ChartComponent.propTypes = {
    type: PropTypes.oneOf(['line', 'bar', 'mixed', 'area', 'horizontalBar', 'stackedBar', 'stackedHorizontalBar', 'pie', 'doughnut', 'radar']).isRequired,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    config: PropTypes.shape({
        graph: PropTypes.object.isRequired,
        data: PropTypes.object.isRequired,
        axis: PropTypes.object.isRequired
    }).isRequired
};


// WEBPACK FOOTER //
// ./src/components/common/chart-component.jsx